import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Page, Block, Button, Segmented, SegmentedButton } from 'konsta/react'
import { MessageCircle, Globe, Phone } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'

// Telegram-style SVG icon
function TelegramIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
    </svg>
  )
}

// Google "G" icon
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  )
}

// Apple icon
function AppleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  )
}

type Language = 'zh-TW' | 'en'

export function LoginScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as { from?: string })?.from || '/'
  const [lang, setLang] = useState<Language>('zh-TW')
  const [isLoading, setIsLoading] = useState<string | null>(null)

  const handleLogin = (provider: string) => {
    setIsLoading(provider)
    setTimeout(() => {
      useAuthStore.getState().login(provider)
      setIsLoading(null)
      navigate(from, { replace: true })
    }, 1200)
  }

  const t = lang === 'zh-TW'
    ? {
        subtitle: '德州撲克俱樂部',
        telegram: '使用 Telegram 登入',
        telegramWidget: 'Telegram Widget 登入',
        phone: '手機 OTP 驗證',
        google: 'Google 登入',
        apple: 'Apple 登入',
        footer: '首次登入即表示同意服務條款與隱私政策',
      }
    : {
        subtitle: 'Texas Poker Club',
        telegram: 'Sign in with Telegram',
        telegramWidget: 'Telegram Widget Login',
        phone: 'Phone OTP',
        google: 'Sign in with Google',
        apple: 'Sign in with Apple',
        footer: 'By signing in, you agree to our Terms of Service and Privacy Policy',
      }

  const loginButtons = [
    {
      id: 'telegram',
      label: t.telegram,
      icon: <TelegramIcon className="w-5 h-5" />,
      filled: true,
    },
    {
      id: 'telegram-widget',
      label: t.telegramWidget,
      icon: <MessageCircle className="w-5 h-5" />,
      filled: true,
    },
    {
      id: 'phone',
      label: t.phone,
      icon: <Phone className="w-5 h-5" />,
      filled: false,
    },
    {
      id: 'google',
      label: t.google,
      icon: <GoogleIcon className="w-5 h-5" />,
      filled: false,
    },
    {
      id: 'apple',
      label: t.apple,
      icon: <AppleIcon className="w-5 h-5" />,
      filled: false,
    },
  ]

  return (
    <Page className="bg-[#F2F2F7]">
      <div className="flex flex-col items-center justify-between min-h-full px-6 py-8">
        {/* Language Selector */}
        <div className="w-full max-w-xs">
          <Segmented strong rounded>
            <SegmentedButton
              active={lang === 'zh-TW'}
              onClick={() => setLang('zh-TW')}
            >
              <span className="flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5" />
                繁體中文
              </span>
            </SegmentedButton>
            <SegmentedButton
              active={lang === 'en'}
              onClick={() => setLang('en')}
            >
              <span className="flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5" />
                English
              </span>
            </SegmentedButton>
          </Segmented>
        </div>

        {/* Logo Area */}
        <div className="flex flex-col items-center gap-2 py-8">
          {/* Logo circle */}
          <div className="w-24 h-24 rounded-full bg-[#007AFF] flex items-center justify-center mb-4 shadow-lg">
            <span className="text-white text-4xl font-bold tracking-tight" style={{ fontFamily: 'Georgia, serif' }}>
              P
            </span>
          </div>
          <h1 className="text-3xl font-bold text-black tracking-tight">
            PWG Lounge
          </h1>
          <p className="text-base text-[#8E8E93] font-medium">
            {t.subtitle}
          </p>
        </div>

        {/* Login Buttons */}
        <div className="w-full max-w-sm flex flex-col gap-3">
          {loginButtons.map((btn) => (
            <Button
              key={btn.id}
              large
              rounded
              className={
                btn.filled
                  ? 'k-color-brand-blue'
                  : ''
              }
              outline={!btn.filled}
              onClick={() => handleLogin(btn.id)}
              disabled={isLoading !== null}
            >
              <span className="flex items-center justify-center gap-2.5 w-full">
                {isLoading === btn.id ? (
                  <span className="w-5 h-5 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                ) : (
                  btn.icon
                )}
                <span>{btn.label}</span>
              </span>
            </Button>
          ))}
        </div>

        {/* Footer */}
        <Block className="text-center !mt-6 !mb-0">
          <p className="text-xs text-[#8E8E93] leading-relaxed">
            {t.footer}
          </p>
        </Block>
      </div>
    </Page>
  )
}
